import { useState, useEffect, useCallback } from "react";
import { FileText, Loader2, AlertCircle, Clock, ArrowLeft, Trash2, X } from "lucide-react";
import api from "../api/api";
import OrgChecklistReportCard from "./OrgChecklistReportCard";

export default function OrgChecklistHistory({ isDarkMode, formatDate, statusBadge }) {
    const [orgChecks, setOrgChecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCheck, setSelectedCheck] = useState(null);
    const [loadingCheck, setLoadingCheck] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [error, setError] = useState("");

    const fetchChecks = useCallback(async () => {
        try {
            const res = await api.get("/resume/my-org-checks");
            setOrgChecks(res.data.orgChecks || []);
        } catch {
            setError("Failed to load checklist history");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchChecks(); }, [fetchChecks]);

    const handleViewCheck = async (orgCheckId) => {
        setLoadingCheck(true);
        setError("");
        try {
            const res = await api.get(`/resume/org-check/${orgCheckId}`);
            setSelectedCheck(res.data);
        } catch {
            setError("Failed to load checklist result");
        } finally {
            setLoadingCheck(false);
        }
    };

    const handleDeleteCheck = async (orgCheckId) => {
        try {
            await api.delete(`/resume/org-check/${orgCheckId}`);
            if (selectedCheck?.OrgCheckID === orgCheckId) setSelectedCheck(null);
            setDeleteConfirm(null);
            fetchChecks();
        } catch {
            setError("Failed to delete checklist result");
            setDeleteConfirm(null);
        }
    };

    const cardClass = `rounded-2xl border shadow-sm p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
        );
    }

    if (selectedCheck) {
        return (
            <section>
                <button
                    onClick={() => setSelectedCheck(null)}
                    className={`flex items-center gap-2 text-sm font-medium mb-4 transition-colors ${
                        isDarkMode ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                    <ArrowLeft size={16} />
                    Back to History
                </button>

                {loadingCheck ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 size={24} className="animate-spin text-primary-500" />
                    </div>
                ) : selectedCheck.Status === "completed" && selectedCheck.ResultsJSON ? (
                    <OrgChecklistReportCard check={selectedCheck} isDarkMode={isDarkMode} />
                ) : (
                    <div className={`${cardClass} text-center`}>
                        <p className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                            {selectedCheck.ResumeLabel || selectedCheck.FileName}
                        </p>
                        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(selectedCheck.Status)}`}>
                            {selectedCheck.Status}
                        </span>
                        <p className={`text-sm mt-3 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {selectedCheck.Status === "processing" && "Your resume is being checked. Check back in a moment."}
                            {selectedCheck.Status === "failed" && (selectedCheck.ErrorMessage || "Something went wrong. Please try again.")}
                        </p>
                    </div>
                )}
            </section>
        );
    }

    return (
        <section>
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4 flex items-center gap-2">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            <div className={cardClass}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <Clock size={20} />
                        Checklist History
                    </h2>
                </div>

                {orgChecks.length > 0 ? (
                    <div className="space-y-3">
                        {orgChecks.map((c) => (
                            <div
                                key={c.OrgCheckID}
                                onClick={() => handleViewCheck(c.OrgCheckID)}
                                className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                                    isDarkMode ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-50 hover:bg-slate-100"
                                }`}
                            >
                                <div className="min-w-0 mr-3">
                                    <p className="text-sm font-semibold truncate">{c.OrgName || c.OrgCode}</p>
                                    <p className={`text-xs truncate ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                        {c.ResumeLabel || c.FileName}
                                    </p>
                                    <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                        {formatDate(c.CreatedOn)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {c.Status === "completed" && c.TotalCount ? (
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            c.PassCount === c.TotalCount
                                                ? isDarkMode ? "bg-green-900/40 text-green-400" : "bg-green-100 text-green-700"
                                                : isDarkMode ? "bg-amber-900/40 text-amber-300" : "bg-amber-100 text-amber-700"
                                        }`}>
                                            {c.PassCount} of {c.TotalCount} met
                                        </span>
                                    ) : (
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge(c.Status)}`}>
                                            {c.Status}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteConfirm({ id: c.OrgCheckID, name: `${c.OrgName || c.OrgCode} · ${c.ResumeLabel || c.FileName}` });
                                        }}
                                        className={`p-1.5 rounded-lg transition-colors ${
                                            isDarkMode ? "text-slate-400 hover:text-red-400 hover:bg-slate-600" : "text-slate-400 hover:text-red-500 hover:bg-slate-200"
                                        }`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                        No checklist results yet. Run your first check from the Resume Checklist tab to see results here.
                    </p>
                )}
            </div>

            {deleteConfirm && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)}>
                    <div
                        className={`w-full max-w-md mx-4 rounded-2xl border shadow-lg p-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <FileText size={18} />
                                Delete checklist result?
                            </h3>
                            <button onClick={() => setDeleteConfirm(null)} className={`p-1 rounded ${isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-400 hover:text-slate-600"}`}>
                                <X size={18} />
                            </button>
                        </div>
                        <p className={`text-sm mb-5 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                            This will remove <span className="font-medium">{deleteConfirm.name}</span> from your history.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${isDarkMode ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteCheck(deleteConfirm.id)}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
